import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import CircularProgress from '@mui/material/CircularProgress';
import { userApi, ApiError } from '../../../services/api';
import styles from './ProfileSubscriptionScreen.module.css';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'monthly' | 'yearly';
  features: string[];
  highlighted?: boolean;
}

interface CurrentSubscription {
  plan: string;
  status: 'active' | 'cancelled' | 'expired';
  nextBillingDate: string;
  amount: number;
  paymentMethod: {
    type: string;
    last4: string;
  };
}

const ProfileSubscriptionScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isChangingPlan, setIsChangingPlan] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      const result = await userApi.getSubscription();
      
      setCurrentSubscription(result.current);
      setAvailablePlans(result.plans);
      setSelectedPlan(result.current?.plan.toLowerCase());
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanChange = async (planId: string) => {
    if (planId === selectedPlan) return;
    
    setIsChangingPlan(true);
    try {
      await userApi.updateSubscription({ planId });
      
      setSelectedPlan(planId);
      // Refresh subscription data
      await fetchSubscriptionData();
    } catch (error) {
      console.error('Error changing plan:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsChangingPlan(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      try {
        await userApi.cancelSubscription();
        await fetchSubscriptionData();
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        if (error instanceof ApiError) {
          console.error('API Error:', error.message);
        }
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className={styles.profileSubscription}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileSubscription}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/profile')}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Subscription</h1>
        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.content}>
        {currentSubscription && (
          <div className={styles.currentPlanSection}>
            <h2 className={styles.sectionTitle}>Current Plan</h2>
            <div className={styles.currentPlanCard}>
              <div className={styles.planHeader}>
                <h3 className={styles.planName}>{currentSubscription.plan}</h3>
                <span className={`${styles.statusBadge} ${styles[currentSubscription.status]}`}>
                  {currentSubscription.status}
                </span>
              </div>
              
              <div className={styles.billingInfo}>
                <div className={styles.billingItem}>
                  <AccessTimeIcon className={styles.billingIcon} />
                  <div>
                    <p className={styles.billingLabel}>Next billing date</p>
                    <p className={styles.billingValue}>{formatDate(currentSubscription.nextBillingDate)}</p>
                  </div>
                </div>
                
                <div className={styles.billingItem}>
                  <PaymentIcon className={styles.billingIcon} />
                  <div>
                    <p className={styles.billingLabel}>Payment method</p>
                    <p className={styles.billingValue}>
                      {currentSubscription.paymentMethod.type} ending in {currentSubscription.paymentMethod.last4}
                    </p>
                  </div>
                </div>
                
                <div className={styles.billingItem}>
                  <StarIcon className={styles.billingIcon} />
                  <div>
                    <p className={styles.billingLabel}>Monthly charge</p>
                    <p className={styles.billingValue}>${currentSubscription.amount}</p>
                  </div>
                </div>
              </div>

              {currentSubscription.status === 'active' && (
                <button className={styles.cancelButton} onClick={handleCancelSubscription}>
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        )}

        <div className={styles.plansSection}>
          <h2 className={styles.sectionTitle}>Available Plans</h2>
          <div className={styles.plansGrid}>
            {availablePlans.map((plan) => (
              <div 
                key={plan.id} 
                className={`${styles.planCard} ${plan.highlighted ? styles.highlighted : ''} ${selectedPlan === plan.id ? styles.selected : ''}`}
              >
                {plan.highlighted && (
                  <div className={styles.popularBadge}>Most Popular</div>
                )}
                
                <h3 className={styles.planCardName}>{plan.name}</h3>
                <div className={styles.planPrice}>
                  <span className={styles.priceAmount}>${plan.price}</span>
                  <span className={styles.priceInterval}>/{plan.interval}</span>
                </div>
                
                <ul className={styles.featuresList}>
                  {plan.features.map((feature, index) => (
                    <li key={index} className={styles.featureItem}>
                      <CheckCircleIcon className={styles.featureIcon} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  className={`${styles.selectPlanButton} ${selectedPlan === plan.id ? styles.currentPlan : ''}`}
                  onClick={() => handlePlanChange(plan.id)}
                  disabled={selectedPlan === plan.id || isChangingPlan}
                >
                  {selectedPlan === plan.id ? 'Current Plan' : 'Select Plan'}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.faqSection}>
          <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
          <div className={styles.faqItem}>
            <h4 className={styles.faqQuestion}>Can I change my plan anytime?</h4>
            <p className={styles.faqAnswer}>
              Yes, you can upgrade or downgrade your plan at any time. Changes will take effect at the start of your next billing cycle.
            </p>
          </div>
          <div className={styles.faqItem}>
            <h4 className={styles.faqQuestion}>What happens when I cancel?</h4>
            <p className={styles.faqAnswer}>
              You'll continue to have access to premium features until the end of your current billing period. After that, you'll be switched to the free plan.
            </p>
          </div>
          <div className={styles.faqItem}>
            <h4 className={styles.faqQuestion}>Can I get a refund?</h4>
            <p className={styles.faqAnswer}>
              We offer a 30-day money-back guarantee for new subscriptions. Contact support for assistance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSubscriptionScreen;